// src/components/apps/SharePointFinance/SharePointFinanceFileBrowser.jsx
import React, { useState, useEffect, useCallback } from "react";
import {
  Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
  Button, CircularProgress, Typography, IconButton, Tooltip, Alert,
  Breadcrumbs, Link,
} from "@mui/material";
import FolderIcon from "@mui/icons-material/Folder";
import InsertDriveFileIcon from "@mui/icons-material/InsertDriveFile";
import DownloadIcon from "@mui/icons-material/Download";
import DeleteIcon from "@mui/icons-material/Delete";
import DriveFileMoveIcon from "@mui/icons-material/DriveFileMove";
import NavigateNextIcon from "@mui/icons-material/NavigateNext";
import HomeIcon from "@mui/icons-material/Home";
import DeleteConfirmDialog from "../SharePoint/DeleteConfirmDialog";
import FolderPickerDialog from "../SharePoint/FolderPickerDialog";
import { listItems, downloadFile, deleteItem, moveItem, listFolders } from "../../../services/sharePointFinanceService";

const SharePointFinanceFileBrowser = ({ refresh, onFolderChange }) => {
  const [pathStack, setPathStack] = useState([]);
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  // Delete dialog state
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [itemToDelete, setItemToDelete] = useState(null);
  const [deleteLoading, setDeleteLoading] = useState(false);

  // Move dialog state
  const [moveDialogOpen, setMoveDialogOpen] = useState(false);
  const [itemToMove, setItemToMove] = useState(null);
  const [moveLoading, setMoveLoading] = useState(false);

  const currentFolderId = pathStack.length > 0 ? pathStack[pathStack.length - 1].id : null;

  useEffect(() => {
    if (onFolderChange) onFolderChange(currentFolderId);
  }, [currentFolderId, onFolderChange]);

  const fetchItems = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const data = await listItems(currentFolderId);
      data.sort((a, b) => {
        const aIsFolder = !!a.folder;
        const bIsFolder = !!b.folder;
        if (aIsFolder !== bIsFolder) return aIsFolder ? -1 : 1;
        return a.name.localeCompare(b.name);
      });
      setItems(data);
    } catch (err) {
      console.error("Error loading SharePoint items:", err);
      setError(err.message);
      setItems([]);
    } finally {
      setLoading(false);
    }
  }, [currentFolderId]);

  useEffect(() => {
    fetchItems();
  }, [fetchItems, refresh]);

  const handleFolderClick = (folder) => {
    setPathStack((prev) => [...prev, { id: folder.id, name: folder.name }]);
  };

  const handleBreadcrumbNavigate = (index) => {
    if (index === -1) {
      setPathStack([]);
    } else {
      setPathStack((prev) => prev.slice(0, index + 1));
    }
  };

  const handleDownload = async (item) => {
    try {
      await downloadFile(item.id, item.name);
    } catch (err) {
      console.error("Download failed:", err);
      setError(`Download failed: ${err.message}`);
    }
  };

  // Delete handlers
  const handleDeleteClick = (item) => {
    setItemToDelete(item);
    setDeleteDialogOpen(true);
  };

  const handleDeleteClose = () => {
    setDeleteDialogOpen(false);
    setItemToDelete(null);
  };

  const handleDeleteConfirm = async () => {
    if (!itemToDelete) return;
    setDeleteLoading(true);
    try {
      await deleteItem(itemToDelete.id);
      setDeleteDialogOpen(false);
      setItemToDelete(null);
      fetchItems();
    } catch (err) {
      console.error("Delete failed:", err);
      setError(`Delete failed: ${err.message}`);
    } finally {
      setDeleteLoading(false);
    }
  };

  // Move handlers
  const handleMoveClick = (item) => {
    setItemToMove(item);
    setMoveDialogOpen(true);
  };

  const handleMoveClose = () => {
    setMoveDialogOpen(false);
    setItemToMove(null);
  };

  const handleMoveConfirm = async (destinationFolderId) => {
    if (!itemToMove) return;
    setMoveLoading(true);
    try {
      await moveItem(itemToMove.id, destinationFolderId);
      setMoveDialogOpen(false);
      setItemToMove(null);
      fetchItems();
    } catch (err) {
      console.error("Move failed:", err);
      setError(`Move failed: ${err.message}`);
    } finally {
      setMoveLoading(false);
    }
  };

  const formatSize = (bytes) => {
    if (!bytes) return "--";
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
    return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
  };

  const formatDate = (isoString) => {
    if (!isoString) return "--";
    return new Date(isoString).toLocaleDateString("en-US", {
      year: "numeric", month: "short", day: "numeric",
      hour: "2-digit", minute: "2-digit",
    });
  };

  if (loading) {
    return <CircularProgress sx={{ display: "block", mx: "auto", my: 4 }} />;
  }

  return (
    <>
      {/* Breadcrumb */}
      <Breadcrumbs separator={<NavigateNextIcon fontSize="small" />} sx={{ mb: 2 }}>
        <Link
          component="button"
          variant="body1"
          underline="hover"
          sx={{ display: "flex", alignItems: "center", cursor: "pointer" }}
          onClick={() => handleBreadcrumbNavigate(-1)}
        >
          <HomeIcon sx={{ mr: 0.5 }} fontSize="small" />
          Finance Documents
        </Link>
        {pathStack.map((folder, index) => {
          const isLast = index === pathStack.length - 1;
          return isLast ? (
            <Typography key={folder.id} color="text.primary" variant="body1">
              {folder.name}
            </Typography>
          ) : (
            <Link
              key={folder.id}
              component="button"
              variant="body1"
              underline="hover"
              sx={{ cursor: "pointer" }}
              onClick={() => handleBreadcrumbNavigate(index)}
            >
              {folder.name}
            </Link>
          );
        })}
      </Breadcrumbs>

      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

      <TableContainer>
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>Name</TableCell>
              <TableCell>Modified</TableCell>
              <TableCell>Modified By</TableCell>
              <TableCell align="right">Size</TableCell>
              <TableCell align="center">Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {items.map((item) => (
              <TableRow key={item.id} hover>
                <TableCell>
                  {item.folder ? (
                    <Button
                      startIcon={<FolderIcon color="warning" />}
                      onClick={() => handleFolderClick(item)}
                      sx={{ textTransform: "none" }}
                    >
                      {item.name}
                    </Button>
                  ) : (
                    <Button
                      startIcon={<InsertDriveFileIcon color="action" />}
                      onClick={() => handleDownload(item)}
                      sx={{ textTransform: "none", color: "text.primary" }}
                    >
                      {item.name}
                    </Button>
                  )}
                </TableCell>
                <TableCell>{formatDate(item.lastModifiedDateTime)}</TableCell>
                <TableCell>
                  {item.lastModifiedBy?.user?.displayName || "--"}
                </TableCell>
                <TableCell align="right">
                  {item.folder ? `${item.folder.childCount} items` : formatSize(item.size)}
                </TableCell>
                <TableCell align="center">
                  {!item.folder && (
                    <Tooltip title="Download">
                      <IconButton size="small" onClick={() => handleDownload(item)}>
                        <DownloadIcon />
                      </IconButton>
                    </Tooltip>
                  )}
                  <Tooltip title="Move">
                    <IconButton size="small" onClick={() => handleMoveClick(item)}>
                      <DriveFileMoveIcon />
                    </IconButton>
                  </Tooltip>
                  <Tooltip title="Delete">
                    <IconButton size="small" onClick={() => handleDeleteClick(item)} color="error">
                      <DeleteIcon />
                    </IconButton>
                  </Tooltip>
                </TableCell>
              </TableRow>
            ))}
            {items.length === 0 && !error && (
              <TableRow>
                <TableCell colSpan={5} align="center">
                  This folder is empty
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </TableContainer>

      {/* Delete Confirmation Dialog */}
      <DeleteConfirmDialog
        open={deleteDialogOpen}
        item={itemToDelete}
        onClose={handleDeleteClose}
        onConfirm={handleDeleteConfirm}
        loading={deleteLoading}
      />

      {/* Folder Picker Dialog for Move */}
      <FolderPickerDialog
        open={moveDialogOpen}
        item={itemToMove}
        onClose={handleMoveClose}
        onConfirm={handleMoveConfirm}
        loading={moveLoading}
        listFolders={listFolders}
        rootLabel="Finance Documents"
        currentFolderId={currentFolderId}
      />
    </>
  );
};

export default SharePointFinanceFileBrowser;